// components/CreditApprovalHistory.js
import React, { useState } from "react";
import { FaGreaterThan, FaLessThan } from "react-icons/fa6";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  useDisclosure,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Card,
  Heading,
  Flex,
  Badge,
  HStack,
  Select,
} from "@chakra-ui/react";

const primaryColor = "#347433";

const CreditApprovalHistory = ({
  creditHistory,
  shouldShowCreditHistory,
  transactionHistory,
}) => {
  const { isOpen: isCreditOpen, onOpen: onCreditOpen, onClose: onCreditClose } = useDisclosure();
  const { isOpen: isTxnOpen, onOpen: onTxnOpen, onClose: onTxnClose } = useDisclosure();

  const [creditPage, setCreditPage] = useState(1);
  const [txnPage, setTxnPage] = useState(1);
  const [creditRowsPerPage, setCreditRowsPerPage] = useState(5);
  const [txnRowsPerPage, setTxnRowsPerPage] = useState(5);

  const getStatusBadge = (status) => {
    switch (status) {
      case "0":
        return { text: "Under Review", color: "yellow" };
      case "1":
        return { text: "Rejected", color: "red" };
      case "2":
        return { text: "Commented", color: "blue" };
      case "3":
        return { text: "Approved", color: "green" };
      default:
        return { text: status, color: "gray" };
    }
  };

  const getControlText = (value) => {
    if (value === "2" || value === 2) return "BLOCK";
    // if (value === "1" || value === 1) return "WARN";
    if (value === "0" || value === 0) return "NONE";
    return value || "WARN";
  };

  const sortedCreditHistory = [...creditHistory].sort(
    (a, b) => new Date(b.datetime) - new Date(a.datetime)
  );
  const sortedTxnHistory = [...transactionHistory].sort(
    (a, b) => new Date(b.orderDate) - new Date(a.orderDate)
  );

  const creditStart = (creditPage - 1) * creditRowsPerPage;
  const creditEnd = creditStart + creditRowsPerPage;
  const paginatedCreditHistory = sortedCreditHistory.slice(creditStart, creditEnd);

  const txnStart = (txnPage - 1) * txnRowsPerPage;
  const txnEnd = txnStart + txnRowsPerPage;
  const paginatedTxnHistory = sortedTxnHistory.slice(txnStart, txnEnd);

  return (
    <>
      <Box mt={6}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
          <Card shadow="md" border="none" rounded="lg" p={4} mb={8}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Credit Approval History</Heading>
              <Button bg={primaryColor} color="white" size="sm" onClick={onCreditOpen}>
                View
              </Button>
            </Flex>
          </Card>

          <Card shadow="md" border="none" rounded="lg" p={4} mb={8}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Transaction History</Heading>
              <Button bg={primaryColor} color="white" size="sm" onClick={onTxnOpen}>
                View
              </Button>
            </Flex>
          </Card>
        </SimpleGrid>
      </Box>

      {/* Credit History Modal */}
      <Modal isOpen={isCreditOpen} onClose={onCreditClose} size="6xl">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" boxShadow="2xl" overflow="hidden">
          <ModalHeader bg={primaryColor} color="white" fontSize="xl" py={4}>
            Credit Approval History
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            {shouldShowCreditHistory && creditHistory.length > 0 ? (
              <>
                <Box overflowX="auto" borderWidth="1px" borderRadius="lg" boxShadow="sm">
                  <Table size="sm" variant="striped" colorScheme="gray">
                    <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
                      <Tr>
                        <Th>Date & Time</Th>
                        <Th>Status</Th>
                        <Th>Credit Limit</Th>
                        <Th>Limit Control</Th>
                        <Th>Credit Days</Th>
                        <Th>Days Control</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {paginatedCreditHistory.map((item) => {
                        const statusBadge = getStatusBadge(item.status);
                        return (
                          <Tr key={item.id}>
                            <Td fontSize="sm" whiteSpace="nowrap">
                              {new Date(item.datetime).toLocaleString()}
                            </Td>
                            <Td>
                              <Badge px={2} py={1} borderRadius="md" colorScheme={statusBadge.color}>
                                {statusBadge.text}
                              </Badge>
                            </Td>
                            <Td>₹{item.credit_limit}</Td>
                            <Td>{getControlText(item.cc_type_limit)}</Td>
                            <Td>{item.credit_days} Days</Td>
                            <Td>{getControlText(item.cd_type_limit)}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>

                {/* Pagination */}
                <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={4}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">
                      Rows per page:
                    </Text>
                    <Select
                      size="sm"
                      w="80px"
                      value={creditRowsPerPage}
                      onChange={(e) => {
                        setCreditRowsPerPage(Number(e.target.value));
                        setCreditPage(1);
                      }}
                    >
                      {[5, 10, 25, 50].map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </Select>
                  </HStack>

                  <HStack spacing={3}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCreditPage((p) => Math.max(p - 1, 1))}
                      isDisabled={creditPage === 1}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {creditPage} of {Math.ceil(creditHistory.length / creditRowsPerPage)}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setCreditPage((p) =>
                          Math.min(p + 1, Math.ceil(creditHistory.length / creditRowsPerPage))
                        )
                      }
                      isDisabled={creditPage >= Math.ceil(creditHistory.length / creditRowsPerPage)}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              </>
            ) : (
              <Text textAlign="center" py={8} color="gray.500" fontSize="md">
                No history available
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCreditClose} colorScheme="purple" borderRadius="lg">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Transaction History Modal */}
      <Modal isOpen={isTxnOpen} onClose={onTxnClose} size="6xl">
        <ModalOverlay />
        <ModalContent borderRadius="2xl" boxShadow="2xl" overflow="hidden">
          <ModalHeader bg={primaryColor} color="white" fontSize="xl" py={4}>
            Transaction History
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            {transactionHistory && transactionHistory.length > 0 ? (
              <>
                <Box overflowX="auto" borderWidth="1px" borderRadius="lg" boxShadow="sm">
                  <Table size="sm" variant="striped" colorScheme="gray">
                  <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
  <Tr>
    <Th>Order ID</Th>
    <Th>Invoice Number</Th>
    <Th>Order Date</Th>
    <Th>Invoice Amount</Th>
    <Th>Payment Mode</Th>
  </Tr>
</Thead>
<Tbody>
  {paginatedTxnHistory.map((txn, index) => (
    <Tr key={txn.id || index}>
      <Td fontSize="sm" whiteSpace="nowrap">{txn.orderid}</Td>
      <Td>{txn.inv_no}</Td>
      <Td>{new Date(txn.datetime).toLocaleDateString()}</Td>
      <Td>₹{txn.totalamount}</Td>
      <Td>{txn.paymentmode}</Td>
    </Tr>
  ))}
</Tbody>
                  </Table>
                </Box>

                {/* Pagination */}
                <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={4}>
                  <HStack spacing={2}>
                    <Text fontSize="sm" color="gray.600">Rows per page:</Text>
                    <Select
                      size="sm"
                      w="80px"
                      value={txnRowsPerPage}
                      onChange={(e) => {
                        setTxnRowsPerPage(Number(e.target.value));
                        setTxnPage(1);
                      }}
                    >
                      {[5, 10, 25, 50].map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </Select>
                  </HStack>

                  <HStack spacing={3}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTxnPage((p) => Math.max(p - 1, 1))}
                      isDisabled={txnPage === 1}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {txnPage} of {Math.ceil(transactionHistory.length / txnRowsPerPage)}
                    </Text>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setTxnPage((p) => Math.min(p + 1, Math.ceil(transactionHistory.length / txnRowsPerPage)))
                      }
                      isDisabled={txnPage >= Math.ceil(transactionHistory.length / txnRowsPerPage)}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              </>
            ) : (
              <Text textAlign="center" py={8} color="gray.500" fontSize="md">
                No transactions available
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onTxnClose} colorScheme="purple" borderRadius="lg">
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreditApprovalHistory;
